import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button, Snackbar, IconButton, CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';

const EditSchoolProfile = () => {
  const { state } = useLocation();
  const { school } = state || {};
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    phoneNumber: school?.phoneNumber || '',
    email: school?.email || '',
    website: school?.website || '',
    address: school?.address || '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleUpdate = async () => {
    if (!formData.phoneNumber || !formData.email || !formData.address) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields (Phone Number, Email, Address)',
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      await axios.put(`https://kidsm.vercel.app/api/schools/${school._id}`, formData);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully',
        severity: 'success',
      });
      setTimeout(() => navigate(-1), 2000); // Navigate back after showing success
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!school) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#e74c3c' }}>
          Error: No school data provided
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: '#6200ee' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50', flexGrow: 1, textAlign: 'center' }}>
            Edit School Profile
          </Typography>
          <Button
            onClick={handleUpdate}
            disabled={loading}
            sx={{
              color: '#6200ee',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '1rem', color: '#333', mb: 0.5 }}>Phone Number</Typography>
          <TextField
            fullWidth
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            type="tel"
            placeholder="Enter phone number"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
            required
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '1rem', color: '#333', mb: 0.5 }}>Email</Typography>
          <TextField
            fullWidth
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            type="email"
            placeholder="Enter email address"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
            required
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '1rem', color: '#333', mb: 0.5 }}>Website</Typography>
          <TextField
            fullWidth
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="Enter website URL"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '1rem', color: '#333', mb: 0.5 }}>Address</Typography>
          <TextField
            fullWidth
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            multiline
            rows={4}
            placeholder="Enter address"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
            required
          />
        </Box>
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        sx={{ bgcolor: snackbar.severity === 'success' ? '#4CAF50' : '#e74c3c' }}
      />
    </Container>
  );
};

export default EditSchoolProfile;